// Plugin loader. Runs once at boot (before BlueprintEditor mounts).
//
// Plugin source layout (one folder per plugin under /plugins/<name>/):
//   plugins/<name>/plugin.json    — manifest (build-time, bundled via glob)
//   plugins/<name>/index.js       — JS entry exporting default { init, tick }
//
// Runtime assets (large, fetched at runtime):
//   public/plugins/<name>/...    — ONNX models, meshes, etc.
//
// Failure isolation: a single plugin's init failure is caught, logged LOUD
// (console.error), and skipped so the other plugins still load — a degraded
// single plugin beats a blank app with no plugins. A failure that leaves the
// whole loader unusable still propagates to the App-level error overlay (per
// the CLAUDE.md / docs/UX_PRINCIPLES.md "no silent fallbacks" rule).

import { registerPluginNode } from './pluginNodeRegistry'
import { buildHost } from './pluginHost'
import { validateManifest } from './pluginManifestSchema'

// Vite build-time enumeration. Each plugin's manifest and entry module
// resolve to bundled imports — no runtime HTTP for code/manifest.
const PLUGIN_MANIFESTS = import.meta.glob('/plugins/*/plugin.json', { eager: true })
const PLUGIN_ENTRIES   = import.meta.glob('/plugins/*/index.js')

const plugins = []
let _loadPromise = null   // singleton — guards against React-StrictMode double-effect, etc.

async function loadOne(name, manifestModule) {
  const manifest = manifestModule.default ?? manifestModule

  // Validate BEFORE registering anything — a bad manifest throws here and the
  // per-plugin isolation in loadAllPlugins logs it LOUD and skips the plugin,
  // never a half-registered node (CLAUDE.md "no silent fallbacks").
  const { ok, errors } = validateManifest(manifest, name)
  if (!ok) {
    throw new Error(`Plugin "${name}" manifest invalid:\n  - ${errors.join('\n  - ')}`)
  }

  // Find the matching entry module.
  const entryKey = `/plugins/${name}/index.js`
  const loader = PLUGIN_ENTRIES[entryKey]
  if (!loader) {
    throw new Error(`Plugin "${name}": no entry module at ${entryKey}`)
  }

  const mod = await loader()
  const instance = mod.default
  if (!instance) {
    throw new Error(`Plugin "${name}": entry module has no default export`)
  }

  const host = buildHost(name, manifest)
  if (typeof instance.init === 'function') {
    await instance.init(host, manifest)
  }

  for (const nodeManifest of manifest.nodes ?? []) {
    registerPluginNode(name, nodeManifest, instance)
  }

  plugins.push({ name, manifest, instance })
  console.log(`[pluginLoader] loaded ${name} v${manifest.version} (${manifest.nodes?.length ?? 0} nodes)`)
}

export async function loadAllPlugins() {
  if (_loadPromise) return _loadPromise
  _loadPromise = (async () => {
    for (const [path, mod] of Object.entries(PLUGIN_MANIFESTS)) {
      // Path looks like "/plugins/<name>/plugin.json"
      const m = path.match(/^\/plugins\/([^/]+)\/plugin\.json$/)
      if (!m) continue
      const name = m[1]
      // Isolate each plugin: one plugin's init failure (e.g. a GPU fault in
      // a plugin's model warmup) must not abort loading of the others. Fail
      // LOUD (console.error) but keep going — a degraded single plugin beats a
      // blank app with no plugins at all.
      try {
        await loadOne(name, mod)
      } catch (err) {
        console.error(`[pluginLoader] plugin "${name}" failed to load — skipping it:`, err)
      }
    }
    return plugins
  })()
  return _loadPromise
}

if (typeof window !== 'undefined') {
  window.__PLUGIN_LOADER__ = { plugins, loadAllPlugins }
  window.__VALIDATE_MANIFEST__ = validateManifest   // used by tests/e2e/ci/plugin-system.spec.js
}
